/* eslint-disable no-restricted-globals */
import { IssueMenuIcon } from '@host/components/nav-section/IssueMenuIcon'
import { RouteItem } from '@host/components/nav-section/types'
import DashboardIcon from '@mui/icons-material/Dashboard'
import {
  ArrowsCrossedIcon,
  GeneralCargoOrderIcon,
  HomeIcon,
  HoldsIcon,
  LaptopIcon,
  OrdersIcon,
  ReportsIcon,
  SettingsIcon,
  TimelineIcon,
  UsersIcon,
} from '@tom-ui/ui'
import { authStore, PATHS } from '@tom-ui/utils'
import Permission from 'modules/utils/src/utils/permission.enum'

interface Props {
  showUnifiedOperatorPage: boolean
  skipVesselTally: boolean
  skipRailTally: boolean
  hasGeneralCargo: boolean
  showContainerJourneyReports: boolean
  isNewYardEnabled: boolean
  equipmentTypes: string[]
}

export const getNavitems = ({
  showUnifiedOperatorPage,
  skipVesselTally,
  skipRailTally,
  hasGeneralCargo,
  showContainerJourneyReports,
  equipmentTypes,
  isNewYardEnabled,
}: Props): RouteItem[] => [
  {
    id: PATHS.dashboard.navId,
    title: PATHS.dashboard.title,
    path: PATHS.dashboard.url,
    icon: <HomeIcon sx={{ width: 24 }} />,
    hidden: ![
      Permission.ReadVisits,
      Permission.ReadRailVisits,
      Permission.EquipmentsAssignment,
      Permission.ReadOrders,
    ].every(permission => authStore.hasPermission(permission)),
  },
  {
    id: PATHS.issues.navId,
    title: PATHS.issues.title,
    path: PATHS.issues.issues.url,
    icon: <IssueMenuIcon />,
    requiredPermission: PATHS.issues.issues.permission,
  },
  {
    id: PATHS.issues.holds.navId,
    title: PATHS.issues.holds.title,
    path: PATHS.issues.holds.url,
    icon: <HoldsIcon sx={{ width: 24 }} />,
  },
  {
    id: PATHS.visits.navId,
    title: PATHS.visits.title,
    path: PATHS.visits.visits.url,
    icon: <ArrowsCrossedIcon sx={{ width: 24 }} />,
    children: [
      {
        id: PATHS.visits.vesselVisits.navId,
        title: PATHS.visits.vesselVisits.title,
        path: PATHS.visits.vesselVisits.url,
        requiredPermission: PATHS.visits.vesselVisits.permission,
      },
      {
        id: PATHS.visits.railVisits.navId,
        title: PATHS.visits.railVisits.title,
        path: PATHS.visits.railVisits.url,
        requiredPermission: PATHS.visits.railVisits.permission,
      },
      {
        id: PATHS.visits.truckVisits.navId,
        title: PATHS.visits.truckVisits.title,
        path: PATHS.visits.truckVisits.url,
        requiredPermission: PATHS.visits.truckVisits.permission,
      },
      {
        id: PATHS.visits.containerVisits.navId,
        title: PATHS.visits.containerVisits.title,
        path: PATHS.visits.containerVisits.url,
        requiredPermission: PATHS.visits.containerVisits.permission,
      },
    ],
  },
  {
    id: PATHS.orders.navId,
    title: PATHS.orders.title,
    path: PATHS.orders.numericReleases.url,
    requiredPermission: PATHS.orders.numericReleases.permission,
    icon: <OrdersIcon />,
    children: [
      {
        id: PATHS.orders.numericReleases.navId,
        title: PATHS.orders.numericReleases.title,
        description: PATHS.orders.numericReleases.title,
        path: PATHS.orders.numericReleases.url,
        requiredPermission: PATHS.orders.numericReleases.permission,
      },
      {
        id: PATHS.orders.nonNumericReleases.navId,
        title: PATHS.orders.nonNumericReleases.title,
        description: PATHS.orders.nonNumericReleases.title,
        path: PATHS.orders.nonNumericReleases.url,
        requiredPermission: PATHS.orders.nonNumericReleases.permission,
      },
      {
        id: PATHS.orders.bookings.navId,
        title: PATHS.orders.bookings.title,
        path: PATHS.orders.bookings.url,
        requiredPermission: PATHS.orders.bookings.permission,
      },
      {
        id: PATHS.orders.serviceOrders.navId,
        title: PATHS.orders.serviceOrders.title,
        path: PATHS.orders.serviceOrders.url,
        requiredPermission: PATHS.orders.serviceOrders.permission,
      },
    ],
  },
  {
    id: PATHS.controlRoom.navId,
    title: PATHS.controlRoom.title,
    path: PATHS.controlRoom.jobs?.url ?? '',
    icon: <LaptopIcon />,
    hidden: ![Permission.ReadJobs, Permission.ReadReeferTemperatures].some(permission =>
      authStore.hasPermission(permission),
    ),
    children: [
      {
        id: PATHS.dashboard.navId,
        title: PATHS.dashboard.title,
        path: PATHS.dashboard.url,
        hidden: ![
          Permission.ReadVisits,
          Permission.ReadRailVisits,
          Permission.EquipmentsAssignment,
          Permission.ReadOrders,
        ].every(permission => authStore.hasPermission(permission)),
      },
      {
        id: PATHS.controlRoom.equipments.navId,
        title: PATHS.controlRoom.equipments.title,
        path: PATHS.controlRoom.equipments.url,
        requiredPermission: PATHS.controlRoom.equipments.permission,
      },
      {
        id: PATHS.controlRoom.operationalEquipment.navId,
        title: PATHS.controlRoom.operationalEquipment.title,
        path: PATHS.controlRoom.operationalEquipment.url,
        requiredPermission: PATHS.controlRoom.operationalEquipment.permission,
      },
      {
        id: PATHS.controlRoom.jobs.navId,
        title: PATHS.controlRoom.jobs.title,
        path: PATHS.controlRoom.jobs.url,
        requiredPermission: PATHS.controlRoom.jobs.permission,
      },
      {
        id: PATHS.controlRoom.reeferMonitoring.navId,
        title: PATHS.controlRoom.reeferMonitoring.title,
        path: PATHS.controlRoom.reeferMonitoring.url,
      },
    ],
  },
  {
    id: PATHS.yard.navId,
    title: PATHS.yard.title,
    path: PATHS.yard.planningDashboard.url,
    requiredPermission: PATHS.yard.permission,
    icon: <DashboardIcon />,
    children: isNewYardEnabled
      ? [
          {
            id: PATHS.yard.view.navId,
            title: PATHS.yard.view.title,
            path: PATHS.yard.view.url,
            requiredPermission: PATHS.yard.permission,
          },
          {
            id: PATHS.yard.containerTurnovers.navId,
            title: PATHS.yard.containerTurnovers.title,
            path: PATHS.yard.containerTurnovers.url,
            requiredPermission: PATHS.yard.permission,
          },
        ]
      : undefined,
  },

  {
    id: PATHS.operators.navId,
    title: PATHS.operators.title,
    path: PATHS.operators.tallyclerk.url,
    icon: <UsersIcon />,
    children: [
      {
        id: PATHS.operators.yardclerk.navId,
        title: PATHS.operators.yardclerk.title,
        path: `${new URL(PATHS.operators.yardclerk.url, location.origin)}`,
        requiredPermission: PATHS.operators.yardclerk.permission,
        hidden: !isNewYardEnabled,
      },
      {
        id: PATHS.operators.tallyclerk.navId,
        title: PATHS.operators.tallyclerk.title,
        path: `${new URL(PATHS.operators.tallyclerk.url, location.origin)}`,
        requiredPermission: PATHS.operators.tallyclerk.permission,
        hidden: skipVesselTally && skipRailTally,
      },
      {
        id: PATHS.operators.gateClerk.navId,
        title: PATHS.operators.gateClerk.title,
        path: `${new URL(PATHS.operators.gateClerk.url, location.origin)}`,
        requiredPermission: PATHS.operators.gateClerk.permission,
      },
      {
        id: PATHS.operators.crane.navId,
        title: PATHS.operators.crane.title,
        path: PATHS.operators.crane.url,
        requiredPermission: PATHS.operators.crane.permission,
      },
      {
        id: PATHS.operators.sts.navId,
        title: PATHS.operators.sts.title,
        path: `${new URL(PATHS.operators.sts.url, location.origin)}`,
        requiredPermission: Permission.StartFinishJobs,
        hidden: showUnifiedOperatorPage || !skipVesselTally || !equipmentTypes.includes('STS'),
      },
      {
        id: PATHS.operators.tt.navId,
        title: PATHS.operators.tt.title,
        path: `${new URL(PATHS.operators.tt.url, location.origin)}`,
        requiredPermission: Permission.StartFinishJobs,
        hidden: showUnifiedOperatorPage || !equipmentTypes.includes('TT'),
      },
      {
        id: PATHS.operators.rtg.navId,
        title: PATHS.operators.rtg.title,
        path: `${new URL(PATHS.operators.rtg.url, location.origin)}`,
        requiredPermission: Permission.StartFinishJobs,
        hidden: showUnifiedOperatorPage || !equipmentTypes.includes('RTG'),
      },
      {
        id: PATHS.operators.rmg.navId,
        title: PATHS.operators.rmg.title,
        path: `${new URL(PATHS.operators.rmg.url, location.origin)}`,
        requiredPermission: Permission.StartFinishJobs,
        hidden: showUnifiedOperatorPage || !equipmentTypes.includes('RMG'),
      },
      {
        id: PATHS.operators.rs.navId,
        title: PATHS.operators.rs.title,
        path: `${new URL(PATHS.operators.rs.url, location.origin)}`,
        requiredPermission: Permission.StartFinishJobs,
        hidden: showUnifiedOperatorPage || !equipmentTypes.includes('RS'),
      },
      {
        id: PATHS.operators.ech.navId,
        title: PATHS.operators.ech.title,
        path: `${new URL(PATHS.operators.ech.url, location.origin)}`,
        requiredPermission: Permission.StartFinishJobs,
        hidden: showUnifiedOperatorPage || !equipmentTypes.includes('ECH'),
      },
      {
        title: PATHS.operators.all.title,
        path: `${new URL(PATHS.operators.all.url, location.origin)}`,
        requiredPermission: PATHS.operators.all.permission,
        hidden: !showUnifiedOperatorPage,
      },
      {
        id: PATHS.operators.generalCargo.navId,
        title: PATHS.operators.generalCargo.title,
        path: `${new URL(PATHS.operators.generalCargo.url, location.origin)}`,
        requiredPermission: PATHS.operators.generalCargo.permission,
        hidden: !hasGeneralCargo,
      },
      {
        id: PATHS.operators.reeferOperator.navId,
        title: PATHS.operators.reeferOperator.title,
        path: `${new URL(PATHS.operators.reeferOperator.url, location.origin)}`,
        requiredPermission: PATHS.operators.reeferOperator.permission,
      },
    ],
  },
  {
    id: PATHS.generalCargo.navId,
    title: PATHS.generalCargo.title,
    path: PATHS.generalCargo.orders.url,
    hidden: !hasGeneralCargo,
    icon: <GeneralCargoOrderIcon />,
    children: [
      {
        id: PATHS.generalCargo.orders.navId,
        title: PATHS.generalCargo.orders.title,
        path: PATHS.generalCargo.orders.url,
      },
      {
        id: PATHS.generalCargo.cargoAreas.navId,
        title: PATHS.generalCargo.cargoAreas.title,
        path: PATHS.generalCargo.cargoAreas.url,
      },
      {
        id: PATHS.generalCargo.inventory.navId,
        title: PATHS.generalCargo.inventory.title,
        path: PATHS.generalCargo.inventory.url,
      },
    ],
  },
  {
    id: PATHS.reports.navId,
    title: PATHS.reports.title,
    path: PATHS.reports.url,
    icon: <ReportsIcon />,
    requiredPermission: PATHS.reports.permission,
    children: showContainerJourneyReports
      ? [
          {
            id: PATHS.reports.navId,
            title: PATHS.reports.title,
            path: PATHS.reports.url,
            requiredPermission: PATHS.reports.permission,
          },
          {
            id: PATHS.reportsV2?.navId,
            title: PATHS.reportsV2?.title,
            path: PATHS.reportsV2?.url,
          },
        ]
      : undefined,
  },
  {
    id: PATHS.masterData.navId,
    title: PATHS.masterData.title,
    path: PATHS.masterData.url,
    icon: <TimelineIcon />,
    requiredPermission: PATHS.masterData.permission,
  },
  {
    id: PATHS.config.navId,
    title: PATHS.config.title,
    path: PATHS.config.yardSetup.url,
    icon: <SettingsIcon />,
    hidden: ![
      Permission.ReadPiers,
      Permission.ReadYardBlocks,
      Permission.ReadRailTracks,
      Permission.ReadBerths,
      Permission.ResetData,
      Permission.Admin,
      Permission.ReadAdminTenant,
      Permission.ReadTenants,
      Permission.ReadUsers,
    ].some(permission => authStore.hasPermission(permission)),
    children: [
      {
        id: PATHS.config.yardSetup.navId,
        title: PATHS.config.yardSetup.title,
        path: PATHS.config.yardSetup.url,
        requiredPermission: PATHS.config.yardSetup.permission,
      },
      {
        id: PATHS.config.piers.navId,
        title: PATHS.config.piers.title,
        path: PATHS.config.piers.url,
        requiredPermission: PATHS.config.piers.permission,
      },
      {
        id: PATHS.config.berths.navId,
        title: PATHS.config.berths.title,
        path: PATHS.config.berths.url,
        requiredPermission: PATHS.config.berths.permission,
        hidden: isNewYardEnabled,
      },
      {
        id: PATHS.config.railTracks.navId,
        title: PATHS.config.railTracks.title,
        path: PATHS.config.railTracks.url,
        hidden: isNewYardEnabled,
      },
      {
        id: PATHS.config.users.navId,
        title: PATHS.config.users.title,
        path: PATHS.config.users.url,
        requiredPermission: PATHS.config.users.permission,
      },
      {
        id: PATHS.config.roles.navId,
        title: PATHS.config.roles.title,
        path: PATHS.config.roles.url,
        requiredPermission: PATHS.config.roles.permission,
      },
      {
        id: PATHS.config.tenants.navId,
        title: PATHS.config.tenants.title,
        path: PATHS.config.tenants.url,
        requiredPermission: PATHS.config.tenants.permission,
      },
      {
        id: PATHS.config.tenantConfiguration.navId,
        title: PATHS.config.tenantConfiguration.title,
        path: PATHS.config.tenantConfiguration.url,
        requiredPermission: PATHS.config.tenantConfiguration.permission,
      },
      {
        id: PATHS.config.reset.navId,
        title: PATHS.config.reset.title,
        path: PATHS.config.reset.url,
        requiredPermission: PATHS.config.reset.permission,
      },
      {
        id: PATHS.config.csvUpload.navId,
        title: PATHS.config.csvUpload.title,
        path: PATHS.config.csvUpload.url,
      },
    ],
  },
]
